import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { gql, useLazyQuery } from "@apollo/client";
import { setUnauthedUserHash, setUserInfo } from "../redux/userStore";
import { useEffect, useMemo, useState } from "react";
import { decryptSymmetric, getExportKey } from "../crypto/utils";
import { fromByteArray } from "base64-js";
import { UserInfo } from "../gql/graphql";
import { generateUsername } from "../utils/usernameGeneration";
import { getUnauthedUserHash } from "../utils/unauthedLocalStorage";

const GET_USER_INFO = gql`
  query GetUserInfo {
    getUserInfo {
      encryptedDecryptionKey
      userID
      username
      profilePhotoURL
      firstName
      lastName
      bio
      link
      profilePlaceholder
    }
  }
`;

const LOGOUT_QUERY = gql`
  query LogoutUseUserInfo {
    logout
  }
`;

const CHECK_LOGGED_IN = gql`
  query CheckLoggedInUseUserInfo {
    isLoggedIn
  }
`;

export function useUserInfo() {
  let decryptionKey = useSelector(
    (state: RootState) => state.user.decryptionKey
  );
  const userID = useSelector((state: RootState) => state.user.userID);
  const username = useSelector((state: RootState) => state.user.username);
  const profilePhotoDownloadURL = useSelector(
    (state: RootState) => state.user.profilePhotoDownloadURL
  );
  const firstName = useSelector((state: RootState) => state.user.firstName);
  const lastName = useSelector((state: RootState) => state.user.lastName);
  const bio = useSelector((state: RootState) => state.user.bio);
  const link = useSelector((state: RootState) => state.user.link);
  const profilePlaceholder = useSelector(
    (state: RootState) => state.user.profilePlaceholder
  );
  const unauthedUserHash = useSelector(
    (state: RootState) => state.user.unauthedUserHash
  );

  const randomUsername = useMemo(() => {
    if (unauthedUserHash) {
      return generateUsername(unauthedUserHash);
    }
    return null;
  }, [unauthedUserHash]);

  const isLoggedIn = useMemo(() => {
    return !!userID;
  }, [userID]);

  const dispatch = useDispatch();
  const [getEncryptedDecryptionKey, { loading }] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: "no-cache",
  });
  const [logoutQuery] = useLazyQuery(LOGOUT_QUERY, { fetchPolicy: "no-cache" });
  const [checkLoggedInQuery] = useLazyQuery(CHECK_LOGGED_IN, {
    fetchPolicy: "no-cache",
  });
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    async function retrieveAndSetDecryptionKey() {
      const result: { data: { getUserInfo: UserInfo } } =
        await getEncryptedDecryptionKey();
      const exportKey = getExportKey();
      if (!exportKey || !result.data) {
        return;
      }
      const decryptionKey = decryptSymmetric({
        secretKey: exportKey,
        encryptedPayload: result.data.getUserInfo.encryptedDecryptionKey,
      });
      dispatch(
        setUserInfo({
          decryptionKey: fromByteArray(decryptionKey),
          ...result.data.getUserInfo,
          profilePhotoDownloadURL: result.data.getUserInfo.profilePhotoURL,
        })
      );
      setSettled(true);
    }
    async function cleanupLogoutState() {
      const isLoggedIn = await checkLoggedInQuery();
      if (isLoggedIn) {
        await logoutQuery();
      }
    }
    if (!decryptionKey && getExportKey()) {
      retrieveAndSetDecryptionKey();
    } else {
      dispatch(setUnauthedUserHash(getUnauthedUserHash()));
      setSettled(true);
      if (!getExportKey()) {
        cleanupLogoutState();
      }
    }
  }, [
    checkLoggedInQuery,
    decryptionKey,
    dispatch,
    getEncryptedDecryptionKey,
    logoutQuery,
  ]);

  return {
    decryptionKey,
    userID,
    username,
    profilePhotoDownloadURL,
    firstName,
    lastName,
    bio,
    loading,
    settled,
    link,
    randomUsername,
    isLoggedIn,
    profilePlaceholder,
  };
}
